/**
 * The vocabulary of `lookup_watch_types[].properties` — the flags a watch type
 * carries, which decide what the ref editor, the brand portal, the RN app and the
 * enrichReference prompt treat as physically present on that kind of watch.
 *
 * Until now these were FREE-TEXT tags typed into the Lookup Manager, so a typo
 * (`showDail`) silently switched a section off with no error. The watch-types
 * editor now picks from this list instead, and anything stored that is not in it
 * is shown as unknown rather than dropped.
 *
 * WHY IT LIVES IN CODE, not in a Firestore lookup: a property does nothing until
 * code maps fields to it (the section registry, the editor's section gates). The
 * list is therefore owned by code; a Firestore copy would be a second list that
 * could disagree with the code's.
 *
 * Two kinds, per the field-relevance design memo (property buckets, decided
 * 2026-09-22):
 *   - `legacy` — the five flags every consumer reads today. Live.
 *   - `bucket` — the physical-property buckets. Tickable now, but INERT until the
 *     registry and editor move onto them (buckets build PR 2/3); they are written
 *     alongside the legacy flags, never instead of them, so nothing changes
 *     behaviour by being ticked early.
 * Buckets that are only ever DERIVED from the ref itself (lume, dive bezel) are
 * not in this list: a type cannot carry them.
 */

export type WatchTypePropertyKind = 'legacy' | 'bucket';
export type WatchTypePropertyGroup = 'face' | 'wear' | 'case' | 'movement';

export interface WatchTypePropertyDef {
  /** The exact string stored in `properties`. */
  id: string;
  label: string;
  description: string;
  kind: WatchTypePropertyKind;
  group: WatchTypePropertyGroup;
}

export const WATCH_TYPE_PROPERTIES: readonly WatchTypePropertyDef[] = [
  // ── Legacy flags — live, read by every consumer today ────────────────────
  { id: 'showDial', label: 'Show dial & hands', description: 'Dial & Hands section in the editor and app; dial questions in the prompt.', kind: 'legacy', group: 'face' },
  { id: 'showModule', label: 'Show module', description: 'Electronic-module section; the prompt asks for elecModule, never calibre.', kind: 'legacy', group: 'movement' },
  { id: 'showMovement', label: 'Show movement', description: 'Calibre movement; the prompt asks for calibre and its tier.', kind: 'legacy', group: 'movement' },
  { id: 'showPocketWatch', label: 'Show pocket watch', description: 'Pocket Watch section (grade, case style, escapement …).', kind: 'legacy', group: 'movement' },
  { id: 'hideStrap', label: 'Hide strap', description: 'No strap or bracelet — handheld or pocket-carried.', kind: 'legacy', group: 'wear' },

  // ── Buckets — physical properties; inert until the bucket registry lands ──
  { id: 'has-hands', label: 'Has hands', description: 'Time shown by hands on a physical dial — the dial face and everything on it.', kind: 'bucket', group: 'face' },
  { id: 'has-lcd', label: 'Has LCD', description: 'A segment LCD/LED display with its own background and text colour.', kind: 'bucket', group: 'face' },
  { id: 'has-screen', label: 'Has screen', description: 'A full-colour graphic screen (smart watches, trackers).', kind: 'bucket', group: 'face' },
  { id: 'has-subdials', label: 'Always has subdials', description: 'Sub-registers on every watch of this type (e.g. a stopwatch). Others derive them from functions.', kind: 'bucket', group: 'face' },
  { id: 'has-date-display', label: 'Date display possible', description: 'A dial-borne date display can exist on this type.', kind: 'bucket', group: 'face' },
  { id: 'has-lugs', label: 'Has lugs', description: 'Wrist-worn on lugs (standard spring bars).', kind: 'bucket', group: 'wear' },
  { id: 'has-strap', label: 'Has strap', description: 'Worn on a strap, bracelet or band.', kind: 'bucket', group: 'wear' },
  { id: 'has-crown', label: 'Has crown', description: 'A winding or setting crown.', kind: 'bucket', group: 'case' },
  { id: 'has-electronic-pushers', label: 'Has electronic pushers', description: 'Pushers that drive electronics — not a crown.', kind: 'bucket', group: 'case' },
  { id: 'has-bezel', label: 'Has bezel', description: 'A distinct bezel ring.', kind: 'bucket', group: 'case' },
  { id: 'has-calibre-movement', label: 'Calibre movement', description: 'Crown-driven movement → a calibre (quartz included).', kind: 'bucket', group: 'movement' },
  { id: 'has-electronic-module', label: 'Electronic module', description: 'Driven by an electronic module.', kind: 'bucket', group: 'movement' },
  { id: 'has-pocket-movement', label: 'Pocket movement', description: 'A pocket-watch movement grade and pocket case style.', kind: 'bucket', group: 'movement' },
];

export const WATCH_TYPE_PROPERTY_IDS: ReadonlySet<string> = new Set(WATCH_TYPE_PROPERTIES.map((p) => p.id));

/** Stored properties that are not in the vocabulary — typos, or retired flags. */
export function unknownWatchTypeProperties(values: readonly string[]): string[] {
  return values.filter((v) => !WATCH_TYPE_PROPERTY_IDS.has(v));
}
